import { useParams } from "react-router-dom";
import { Container, LoadingBlock, Button } from "@/components/atoms";
import { ProductCard, Breadcrumb, EmptyState } from "@/components/molecules";
import { useProducts } from "@/hooks/use-products";
import { useCategory } from "@/hooks/use-categories";
import { Seo } from "@/components/seo";

// Category template (frame 96:2) — category hero + filtered product grid.
export default function Category() {
  const { slug = "" } = useParams();
  const { data: category, isLoading: catLoading } = useCategory(slug);
  const { data: products, isLoading } = useProducts(slug);

  if (catLoading || isLoading) return <LoadingBlock />;

  if (!category) {
    return (
      <Container className="py-16">
        <EmptyState
          title="Category not found"
          description="That collection doesn’t exist."
          action={<Button to="/shop">Browse all products</Button>}
        />
      </Container>
    );
  }

  // Single source for "is this collection empty" — the head and the body must not be able to
  // disagree about it, or a page could render "Coming soon" while telling Google it is indexable.
  const isEmpty = (products ?? []).length === 0;

  return (
    <>
      {/* DRB-72 (SEO HIGH) — an empty category renders "Coming soon" and was still submitted to
          Google at the same 0.8 priority as a populated one, with no robots directive. 6 of the 8
          categories are empty, so most of what the sitemap advertised was a placeholder page.
          Thin content indexed at full priority competes with the pages that can actually sell.

          noindex only while it is empty: the moment she adds a product the page becomes worth
          indexing again and this flips back on its own, with no sitemap edit and no deploy. Every
          other thin route in this app already does exactly this (search, not-found, account,
          order-confirmed); category was the one that did not. */}
      <Seo
        title={category.name}
        path={`/shop/${category.slug}`}
        description={category.description}
        noindex={isEmpty}
      />

      <section className="bg-gradient-to-br from-medical-grey to-leaf-green/5 py-10">
        <Container>
          <Breadcrumb
            items={[
              { label: "Home", to: "/" },
              { label: "Shop", to: "/shop" },
              { label: category.name },
            ]}
          />
          <h1 className="mt-4 font-condensed text-4xl font-bold text-night-blue">
            {category.name}
          </h1>
          {category.heroTagline && (
            <p className="mt-1 text-lg text-leaf-green-ink">
              {category.heroTagline}
            </p>
          )}
          <p className="mt-2 max-w-xl text-night-blue/70">
            {category.description}
          </p>
        </Container>
      </section>

      <Container className="py-10">
        {isEmpty ? (
          <EmptyState
            title="Coming soon"
            description="We’re adding products to this collection."
            action={<Button to="/shop">Browse all products</Button>}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {(products ?? []).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </Container>
    </>
  );
}
